import { withTriggerCombobox } from '@platejs/combobox';
import type { TriggerComboboxPluginOptions } from '@platejs/combobox';
import { createSlatePlugin, createTSlatePlugin } from 'platejs';
import type { PluginConfig, SlateEditor } from 'platejs';
import type { Apollo } from 'src/gql/generated';

import { SKILL_TRIGGER_PREVIOUS_CHAR_PATTERN } from '../../ChatEditor/ChatEditorSkill/skill-trigger';

/** Node key for the skill chip. */
export const ELEMENT_SKILL = 'skill';

/** Node key for the transient input the `/` trigger creates. */
export const ELEMENT_SKILL_INPUT = 'skill_input';

/**
 * Transient node the trigger creates; replaced by a chip on select, or reverted to literal
 * text when the picker is cancelled.
 */
export const BaseSkillInputPlugin = createSlatePlugin({
  key: ELEMENT_SKILL_INPUT,
  node: { isElement: true, isInline: true, isVoid: false },
  options: { hasPicker: false },
});

type SkillPluginConfig = PluginConfig<
  typeof ELEMENT_SKILL,
  TriggerComboboxPluginOptions & {
    /**
     * The scopes the picker offers, or null for every skill the user can read. Set for text
     * someone other than its author runs, such as a task description, to the skills that
     * reader can use.
     */
    offeredScopes?: readonly Apollo.AgentSkillScope[] | null;
  }
>;

/**
 * Framework-agnostic `/`-triggered skill plugin.
 *
 * Mirrors `BaseMentionPlugin`: the element plugin carries the combobox options and nests
 * the input plugin, then overrides the editor with `withTriggerCombobox`. It cannot reuse
 * `MentionPlugin` with a different trigger. That plugin is keyed `mention` with input node
 * `mention_input`, and a plugin key can only be registered once.
 */
export const BaseSkillElementPlugin = createTSlatePlugin<SkillPluginConfig>({
  key: ELEMENT_SKILL,
  node: { isElement: true, isInline: true, isMarkableVoid: true, isVoid: true },
  options: {
    createComboboxInput: () => ({
      children: [{ text: '' }],
      type: ELEMENT_SKILL_INPUT,
    }),
    offeredScopes: null,
    trigger: '/',
    triggerPreviousCharPattern: SKILL_TRIGGER_PREVIOUS_CHAR_PATTERN,
    triggerQuery: getHasSkillPicker,
  },
  plugins: [BaseSkillInputPlugin],
}).overrideEditor(withTriggerCombobox);

/**
 * Whether the skill plugin is registered. Plate resolves a throwaway plugin for a key the
 * editor does not have, so read its options, or subscribe to them, only once this is true.
 */
export function getHasSkillPlugin(editor: SlateEditor): boolean {
  return Boolean(editor.plugins[ELEMENT_SKILL]);
}

/** Chip-only editors retain links without offering an input they cannot render. */
export function getHasSkillPicker(editor: SlateEditor): boolean {
  return getHasSkillPlugin(editor) && editor.getOptions(BaseSkillInputPlugin).hasPicker === true;
}
